<script>
	import { onMount } from "svelte";
	import { fly } from "svelte/transition";
	import { interpolate } from "d3-interpolate";
	import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
	import centerOfMass from "@turf/center-of-mass";

	import Buttons from "./components/Buttons.svelte";
	import Search from "./components/Search/Search.svelte";
	import MediaQuery, { platform } from "./components/MediaQuerySsr.svelte";
	import Map from "./map/map";
	import TooltipSection from "./components/TooltipSection.svelte";
	import Legend from "./components/Legend.svelte";

	import { loadJSON, loadMapbox, loadTopojson, scrollTo, getStripes, round } from "./utils";
	import {
		initialBounds,
		maxBounds,
		initialSelectedLocation,
		baseChoroplethOpacity,
		baseChoroplethOpacityLuftbilder,
		basePointsOpacity,
		basePointsStrokeOpacity,
		bezirke,
		markerData,
		toggleButtons,
		toggleButtonsById,
		datesStrings,
		colorScaleTransparent,
	} from "./constants";
	// import values from "./data/values.json";
	import { convertToGeoJSON, circleScale } from "./map/mapbox_helpers";

	// ///////////////////////////////////////////////////////////
	// props
	// ///////////////////////////////////////////////////////////
	export let name;
	export let paywall = false;

	// ///////////////////////////////////////////////////////////
	// screenshot mode
	// ///////////////////////////////////////////////////////////
	const SCREENSHOT_MODE = false;
	const PWIMAGE_MODE = false;
	// PW IMAGES WIDTHS: L --> desktop, m --> around 800px, s --> around 4400px

	$: if (mapLoaded) {
		if (SCREENSHOT_MODE) {
			map.hideLayer("labels-lors");
			map.hideLayer("labels-bezirke");
			// hideRing();
		}
	}

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////

	let map;
	let tooltip;

	let mapZoom;

	let mapActivated = false;

	let mapLoaded = paywall;

	let labelsLorsId = "labels-lors";
	let labelsLors = [];
	let labelsOrtsteile = [];
	let labelsBezirke = [];

	let highlightingColor = "#000";

	let showLuftbilder = false;
	const luftbilderId = "luftbilder";
	let choroplethOpacity = baseChoroplethOpacity;

	const choroplethId = "bike-thefts-relative";
	const pointsId = "bike-thefts-absolute";
	const pointsValueKey = "year_pp_total";

	let geoBikeThefts;
	let geoBezirke;
	let geoOrtsteile;
	let geoRing;
	let absoluteBikeThefts = [];

	// ///////////////////////////////////////////////////////////
	// ui bound parameters
	// ///////////////////////////////////////////////////////////
	export let elemMap;

	let selectedToggleId = "year_pp";

	$: selectedToggle = toggleButtons.find((b) => b.id === selectedToggleId);
	$: selectedToggleIndex = toggleButtons.findIndex((b) => b.id === selectedToggleId);

	let currentGeometry;
	let selectedRegion;
	let selectedLocation;
	let hoveredRegion;

	// ///////////////////////////////////////////////////////////
	// set opacity
	// ///////////////////////////////////////////////////////////
	$: luftbilderChoroplethOpacity = showLuftbilder ? baseChoroplethOpacityLuftbilder : baseChoroplethOpacity;
	$: choroplethOpacity = luftbilderChoroplethOpacity;

	// ///////////////////////////////////////////////////////////
	// update data colors and opacity
	// ///////////////////////////////////////////////////////////

	$: if (mapActivated) {
		if (map.map.getLayer(choroplethId)) map.map.setPaintProperty(choroplethId, "fill-opacity", choroplethOpacity);

		map.setChoroplethData(choroplethId, [0], {
			colorScale: selectedToggle.visType === "choropleth" ? selectedToggle.colorScale : colorScaleTransparent,
			colorScaleValue: selectedToggleId,
		});
	}

	// ///////////////////////////////////////////////////////////
	// show/hide luftbilder
	// ///////////////////////////////////////////////////////////
	$: highlightingColor = showLuftbilder ? "#fff" : "#000";

	$: if (mapActivated) {
		if (showLuftbilder) {
			map.showLayer(luftbilderId);
		} else {
			map.hideLayer(luftbilderId);
		}
		if (map.map.getLayer(choroplethId)) map.map.setPaintProperty(choroplethId, "fill-opacity", choroplethOpacity);

		const idHover = `${choroplethId}-hovering`;
		const idSelected = `${choroplethId}-selected`;

		if (map.map.getLayer(idHover)) map.map.setPaintProperty(idHover, "line-color", highlightingColor);
		if (map.map.getLayer(idSelected)) map.map.setPaintProperty(idSelected, "line-color", highlightingColor);

		map.updateChoroplethOpts(choroplethId, {
			hoveringBorderColor: highlightingColor,
			selectedBorderColor: highlightingColor,
		});
	}

	// ///////////////////////////////////////////////////////////
	// data processing
	// ///////////////////////////////////////////////////////////

	const renameAndDeleteProperties = (obj) => {
		const result = { ...obj }; // Create a shallow copy of the object
		for (const key of Object.keys(obj)) {
			// Use a regular expression to match keys like "March_2022_abs"
			const match = key.match(/^(\w+)_(\d+)_abs$/);
			if (match) {
				const month = match[1];
				const year = match[2];
				// Create the new key by combining the month and year with a space
				const newKey = `${month} ${year}`;
				result[newKey] = result[key];
				delete result[key];
			}
			const matchRel = key.match(/^(\w+)_(\d+)_rel$/);
			if (matchRel) delete result[key];
		}
		return result;
	};

	function calculateValues(geoBikeThefts, toggleButtons) {
		const results = {};

		toggleButtons.forEach((button) => {
			const property = button.id;
			const values = geoBikeThefts.features.map((feature) => feature.properties[property]);
			const valuesInRingTrue = values.filter((value, index) => geoBikeThefts.features[index].properties.ring === true);
			const valuesInRingFalse = values.filter(
				(value, index) => geoBikeThefts.features[index].properties.ring === false
			);

			results[property] = {
				min: Math.min(...values),
				max: Math.max(...values),
				avg: values.reduce((sum, value) => sum + value, 0) / values.length,
				median: values.sort((a, b) => a - b)[Math.floor(values.length / 2)],
				avg_in_ring: valuesInRingTrue.reduce((sum, value) => sum + value, 0) / valuesInRingTrue.length,
				avg_out_ring: valuesInRingFalse.reduce((sum, value) => sum + value, 0) / valuesInRingFalse.length,
			};
		});

		return results;
	}

	const processData = () => {
		geoBikeThefts.features.forEach((f) => {
			f.properties = renameAndDeleteProperties(f.properties);
			f.properties.id = f.properties.lor;

			f.properties.filtered = false;

			if (f.properties.change === "inf") f.properties.change = 1;
			if (f.properties.change === "-inf") f.properties.change = -1;
			f.properties.change = +f.properties.change;

			let countData = 0;
			datesStrings.forEach((d, i) => {
				countData += f.properties[d];
			});
			if (countData === 0) f.properties.noData = true;

			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			labelsLors.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});

			absoluteBikeThefts.push({
				...f.properties,
				lat: f.properties.centerLat,
				lon: f.properties.centerLng,
			});
		});

		const values = calculateValues(geoBikeThefts, toggleButtons);
		console.log("VALUES", values);

		toggleButtons.forEach((b) => {
			b.avgInRing = values[b.id].avg_in_ring;
			b.avgOutRing = values[b.id].avg_out_ring;
		});

		geoBezirke.features.forEach((f) => {
			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.name = bezirke[f.properties["BEZ"]];

			labelsBezirke.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoOrtsteile.features.forEach((f) => {
			labelsOrtsteile.push({
				name: `${f.properties.name}`,
				lnglat: f.geometry.coordinates,
			});
		});
	};

	// ///////////////////////////////////////////////////////////
	// remove label for hovered/selected region
	// ///////////////////////////////////////////////////////////
	$: if (mapActivated) {
		labelsLors.forEach((l) => {
			l.opacity =
				(hoveredRegion && l.name === hoveredRegion.name) || (selectedRegion && l.name === selectedRegion.name) ? 0 : 1;
		});

		if (map.map.getLayer(labelsLorsId)) map.updateLabels(labelsLors, labelsLorsId);
	}

	// ///////////////////////////////////////////////////////////
	// show/hide layers based on the current vis type
	// ///////////////////////////////////////////////////////////

	$: if (mapActivated && mapLoaded) {
		const { visType } = selectedToggle;

		// Set visibility and opacity based on selected visualization type
		switch (visType) {
			case "choropleth":
				// map.showChoropleth(choroplethId, true, ["hovering", "selected"]); // we can't hide the main choropleth layer because then the interaction won't work
				map.map.setPaintProperty(choroplethId, "fill-opacity", choroplethOpacity);
				map.map.setPaintProperty(pointsId, "circle-opacity", 0);
				map.map.setPaintProperty(pointsId, "circle-stroke-opacity", 0);
				break;
			case "points":
				map.showPoints(pointsId, true);
				map.map.setPaintProperty(choroplethId, "fill-opacity", 0);
				map.map.setPaintProperty(pointsId, "circle-opacity", basePointsOpacity);
				map.map.setPaintProperty(pointsId, "circle-stroke-opacity", basePointsStrokeOpacity);
				break;
		}

		// Hide layers not related to the selected visualization type after a delay
		setTimeout(() => {
			// if (selectedToggle.visType !== "choropleth") map.showChoropleth(choroplethId, false, ["hovering", "selected"]);  // we can't hide the main choropleth layer because then the interaction won't work
			if (selectedToggle.visType !== "points") map.showPoints(pointsId, false);
		}, 700);
	}

	// ///////////////////////////////////////////////////////////
	// define options for points and choropleth
	// ///////////////////////////////////////////////////////////

	let tooltipHovered = false;

	const mousemove = (f, filtered) => {
		if (isTouch) return;
		hoveredRegion = f.properties;
		map.hoverPoint(f);
		addPopup(f);
	};

	const mouseleave = () => {
		setTimeout(() => {
			if (!tooltipHovered) {
				hoveredRegion = undefined;
				removePopup();
				map.hoverPoint(false);
				if (selectedRegion) addPopup(selectedRegion);
			}
		});
	};

	const choroplethOpts = {
		geoType: "geojson",
		borderColor: "#FFF",
		borderWidth: 0.5,
		borderOpacity: 1,
		opacity: choroplethOpacity,
		dataType: "colorScale",
		colorScale: selectedToggle?.colorScale,
		colorScaleValue: selectedToggleId,
		data: [0],
		interactive: true,
		zoomOnClick: true,
		zoomLevel: 12.7,
		hoveringBorderColor: highlightingColor,
		selectedBorderColor: highlightingColor,
		filteredColor: "transparent",
		filteredBorderColor: "transparent",
		choroplethClickCallback: (f, filtered, lngLat) => {
			if (!filtered) selectLocation(lngLat, f);
		},
		backgroundClickCallback: () => {
			clearLocation();
		},
		mousemoveCallback: mousemove,
		mouseleaveCallback: mouseleave,
	};

	// calculating weird scales for radius and label offset
	const labelOffsetDivisor = 10;
	const radii = {
		min: 0,
		"8": 0.4,
		"12": 1,
		"16": 6,
	};

	const pointsOpts = {
		id: pointsId,
		identifier: "lor",
		labels: false,
		labelMinZoom: 14.5,
		selectMinZoom: 20,
		labelOffset: circleScale({
			key: pointsValueKey,
			min: 0.8,
			scale: [
				[8, radii["8"] / labelOffsetDivisor],
				[12, radii["12"] / labelOffsetDivisor],
				[16, radii["16"] / labelOffsetDivisor],
			],
		}),
		labelKey: "name",
		belowLabels: true,
		tooltips: false,
		sortKeyCircles: pointsValueKey,
		tooltipsOnlyDesktop: true,
		// mouseenterCallback: mousemove,
		// mouseleaveCallback: mouseleave,
		// onSelect: (f) => selectLocation({ lng: f.properties.lon, lat: f.properties.lat }, f),
		// onReset: () => clearLocation(),
	};

	// ///////////////////////////////////////////////////////////
	// initialize map
	// ///////////////////////////////////////////////////////////

	let isTouch = false;

	onMount(async () => {
		if (paywall) return;

		const promises = [
			loadJSON(`${PATH}assets/data/bikethefts.min.topo.json`),
			loadJSON(`${PATH}assets/data/berlin_bezirke.geo.json`),
			loadJSON(`${PATH}assets/data/ring.geo.json`),
			loadJSON(`${PATH}assets/data/ortsteile_labels.geo.json`),
			loadTopojson(),
			loadMapbox(),
		];

		function checkTouch() {
			isTouch = true;
			document.querySelector("body").removeEventListener("touchstart", checkTouch);
		}
		document.querySelector("body").addEventListener("touchstart", checkTouch);

		let topoBikeThefts;
		[topoBikeThefts, geoBezirke, geoRing, geoOrtsteile] = await Promise.all(promises);

		geoBikeThefts = convertToGeoJSON(topoBikeThefts);

		processData();

		currentGeometry = geoBikeThefts;

		map = new Map(elemMap, {
			useFranklin: true,
			initialBounds: Object.keys(initialBounds).includes($platform)
				? initialBounds[$platform]
				: initialBounds["desktop"],
			maxBounds,
			maxZoom: 19,
			controls: !SCREENSHOT_MODE && !PWIMAGE_MODE,
			controlsLeft: true,
			scale: true,
			popup: false,
			popupShift: false,
			labels: !SCREENSHOT_MODE,
			styleJson: `${PATH}assets/style.json`,
			hideBackgroundMap: SCREENSHOT_MODE,
		});

		map.activated
			.then(() => map.loaded)
			.then(() => {
				const REFLAYER = map.firstLabelLayer;

				map.hideAttribution();

				mapZoom = map.map.getZoom();
				map.map.on("zoom", () => {
					mapZoom = map.map.getZoom();
				});

				map.map.loadImage(getStripes("#ccc", 4, "#fff"), (error, image) => {
					map.map.addImage("stripes-nodata", image);
				});

				// luftbilder
				map.map.addSource(luftbilderId, {
					type: "raster",
					url: "https://interaktiv.tagesspiegel.de/maptiles/luftbilder_2022/tiles.json",
				});

				map.map.addLayer(
					{
						id: luftbilderId,
						source: luftbilderId,
						type: "raster",
					},
					REFLAYER
				);

				// choropleth
				map.addChoropleth(currentGeometry, {
					...choroplethOpts,
					id: choroplethId,
					identifier: "lor",
					hoveringBorderWidth: $platform === "mobile" ? 1.5 : 3,
					selectedBorderWidth: $platform === "mobile" ? 1.5 : 3,
					colorScale: selectedToggle.colorScale,
					colorScaleValue: selectedToggleId,
					noDataColor: "#fff",
					noDataPattern: "stripes-nodata",
					beforeLayer: REFLAYER,
					zoomPadding: () => {
						return {
							top: 0,
							left: 0,
							bottom: window.innerWidth <= 544 ? 150 : 0,
							right: window.innerWidth <= 544 ? 0 : 290,
						};
					},
				});

				// bezirke
				map.addChoropleth(geoBezirke, {
					geoType: "geojson",
					id: "bezirke",
					borderColor: "#FFF",
					borderWidth: $platform === "mobile" ? 1 : 3,
					interactive: false,
					opacity: 0,
					beforeLayer: REFLAYER,
				});

				// points
				map.addPoints(absoluteBikeThefts, {
					...pointsOpts,
					circlePaint: {
						"circle-radius": circleScale({
							key: pointsValueKey,
							min: radii.min,
							scale: [
								[8, radii["8"]],
								[12, radii["12"]],
								[16, radii["16"]],
							],
						}),
						"circle-color": toggleButtonsById[pointsValueKey].mapboxColorExpression,
						"circle-stroke-color": "#ffffff",
						"circle-stroke-width": ["interpolate", ["linear"], ["zoom"], 9, 0.5, 15, 2],
						"circle-opacity": selectedToggle.visType === "points" ? basePointsOpacity : 0,
						"circle-opacity-transition": {
							duration: 700,
						},
						"circle-stroke-opacity": selectedToggle.visType === "points" ? basePointsStrokeOpacity : 0,
					},
				});

				// labels

				const labelsOpts = {
					minZoom: 12.5,
					maxZoom: 14.5,
					fontSize: 12,
					color: "#fff",
					haloWidth: 1,
					haloColor: "#000",
					letterSpacing: 0.015,
					font: "Franklin Gothic Medium",
				};

				map.addLabels(labelsLors, {
					...labelsOpts,
					id: labelsLorsId,
					textAllowOverlap: true,
					textAnchor: "top",
				});

				map.addLabels(labelsOrtsteile, {
					...labelsOpts,
					id: "labels-ortsteile",
					minZoom: 11.5,
					maxZoom: 12.5,
				});

				map.addLabels(labelsBezirke, {
					...labelsOpts,
					id: "labels-bezirke",
					minZoom: 9,
					maxZoom: 11.5,
				});

				showBerlinMarkers();
				showRing();

				mapActivated = true;

				requestAnimationFrame(() => {
					map.map.moveLayer("water", "bezirke");
				});

				map.map.once("idle", () => {
					mapLoaded = true;

					// preselect a point
					selectLocation(initialSelectedLocation, undefined, true);
				});
			});
	});

	// ///////////////////////////////////////////////////////////
	// map selection
	// ///////////////////////////////////////////////////////////
	let marker = null;
	let popup = null;

	const removeMarker = () => {
		if (marker) marker.remove();
	};

	const addMarker = (lon, lat) => {
		if (marker) marker.remove();
		marker = new window.mapboxgl.Marker({
			// anchor: 'bottom',
			color: "#1f1f1f",
			scale: 0.7,
		})
			.setLngLat([lon, lat])
			.addTo(map.map);
	};

	const removePopup = () => {
		if (popup) popup.remove();
	};

	const addPopup = (feature) => {
		const props = feature.properties ? feature.properties : feature;

		if (popup) popup.remove();

		const html = `
				<div class="name">${props.name}</div>
				<div class="description">in ${props.bezirk}</div>
		`;

		popup = new window.mapboxgl.Popup({
			color: "#1f1f1f",
			scale: 0.7,
			closeButton: false,
			closeOnClick: false,
			closeOnMove: false,
			className: "region-popup",
		})
			.setLngLat([props.centerLng, props.centerLat])
			.setHTML(html)
			.addTo(map.map);
	};

	const findFeature = (lon, lat) => {
		let feature = null;
		// first try mapbox, only works if feature is visible
		// then search in the geometry data
		const point = map.map.project([lon, lat]);

		const fs = map.map.queryRenderedFeatures(point, {
			layers: [choroplethId],
		});

		if (fs.length === 1) {
			feature = fs[0];
		} else {
			const pointf = {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: [lon, lat],
				},
			};
			for (let i = 0; i < currentGeometry.features.length; i++) {
				try {
					if (booleanPointInPolygon(pointf, currentGeometry.features[i])) {
						feature = currentGeometry.features[i];
						break;
					}
				} catch (err) {
					console.log(err);
				}
			}
		}
		return feature;
	};

	const selectRegion = (newRegion) => {
		if (!selectedRegion || !newRegion) {
			selectedRegion = newRegion;
		} else {
			const alreadySelected = newRegion === selectedRegion;

			selectedRegion = alreadySelected && !selectedLocation ? undefined : newRegion;
		}

		stopSearch(selectedRegion ? selectedRegion.name : false);
	};

	const clearLocation = () => {
		selectedLocation = null;
		selectedRegion = null;
		hoveredRegion = null;
		tooltip = undefined;
		removeMarker();
		removePopup();
		map.selectChoropleth(choroplethId, false, true);
		map.resetPoints(true);
		selectRegion(false);
	};

	// feature is optional
	const selectLocation = (lngLat, optionalFeature, stayWhereYouAre) => {
		let feature; // = selectedRegion;
		let featureProperties;
		if (lngLat) {
			selectedLocation = lngLat;
			// addMarker(lngLat.lng, lngLat.lat);
		}
		if (optionalFeature) {
			feature = optionalFeature;
		} else if (lngLat) {
			feature = findFeature(lngLat.lng, lngLat.lat);
		} else if (selectedLocation) {
			feature = findFeature(selectedLocation.lng, selectedLocation.lat);
		}
		if (feature && feature.properties !== selectedRegion) {
			// If a feature exists and its properties don't match the selected region
			addPopup(feature);
			selectRegion(feature.properties);
			map.selectChoropleth(choroplethId, null, true);
			map.selectChoropleth(choroplethId, feature.properties.id, stayWhereYouAre);
			map.selectPoint(feature, true, undefined, true);
		} else if (!feature) {
			// If no feature exists
			clearLocation();
		}
	};

	const scrollToTopOfMap = () => {
		scrollTo(elemMap, -90);
	};

	const clearSelection = () => {
		clearLocation();
		scrollToTopOfMap();
	};

	// ///////////////////////////////////////////////////////////
	// markers
	// ///////////////////////////////////////////////////////////

	const berlinMarkers = [];

	function showBerlinMarkers() {
		map.activated
			.then(() => map.loaded)
			.then(() => {
				markerData.forEach((d) => {
					const melc = document.createElement("div");
					const mel = document.createElement("div");
					melc.appendChild(mel);
					mel.classList.add("marker");
					mel.classList.add(`marker--${d.n}`);
					mel.innerHTML = d.label || "";
					berlinMarkers.push(new window.mapboxgl.Marker(melc).setLngLat([d.lon, d.lat]).addTo(map.map));
				});
			});
	}

	let ringshown = false;
	let ringshownonce = false;
	let ringmarker;
	function showRing() {
		if (ringshown) return;
		ringshown = true;
		if (!ringshownonce) {
			ringshownonce = true;
			map.activated
				.then(() => map.loaded)
				.then(() => {
					map.map.addSource("sbahnring", {
						type: "geojson",
						data: geoRing,
					});
					map.map.addLayer({
						id: "sbahnring1",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#ffffff",
							"line-width": 3,
						},
					});
					map.map.addLayer({
						id: "sbahnring2",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#1f1f1f",
							"line-width": 2,
							"line-dasharray": [1, 1],
						},
					});
					const pos = [13.366688444522936, 52.543837986025096];
					const el = document.createElement("div");
					el.className = "ring-marker";
					el.innerHTML = !SCREENSHOT_MODE ? '<div class="ring-marker-text">S-Bahn<br>Ring</div>' : "";
					ringmarker = new window.mapboxgl.Marker(el).setLngLat(pos);
					ringmarker.addTo(map.map);
				});
		} else {
			map.map.setLayoutProperty("sbahnring1", "visibility", "visible");
			map.map.setLayoutProperty("sbahnring2", "visibility", "visible");
			ringmarker.addTo(map.map);
		}
	}

	function hideRing() {
		if (!ringshown) return;
		map.map.setLayoutProperty("sbahnring1", "visibility", "none");
		map.map.setLayoutProperty("sbahnring2", "visibility", "none");
		ringmarker.remove();
		ringshown = false;
	}

	// ///////////////////////////////////////////////////////////
	// search
	// ///////////////////////////////////////////////////////////
	let stopSearch;

	// ///////////////////////////////////////////////////////////
	// tooltip
	// ///////////////////////////////////////////////////////////

	const closeClick = () => clearLocation();

	function tooltipClick() {
		// clearLocation();
	}

	let tooltipExpanded = true;

	function tooltipMouseover() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = true;
		if (selectedRegion) {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}
	}

	function tooltipMouseout() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = false;
	}

	$: console.log("selected", selectedRegion);
</script>

<MediaQuery />
<div class="bike-theft-map" class:pw={paywall}>
	<div class="headline">Finde deinen Kiez</div>
	<div class="ui-container-top">
		<div class="buttons-and-filter">
			<Buttons bind:value={selectedToggleId} options={toggleButtons} let:option let:active>
				<div class="button-main" class:active>
					<span data-title={option.label}>{option.label}</span>
				</div>
			</Buttons>
		</div>
		<div class="search">
			<Search
				type={["geo", "data"]}
				iconGeo={"pin"}
				iconData={"pin"}
				geoSearchBounds={initialBounds["mobile"]}
				dataSearchData={currentGeometry}
				dataSearchKeys={["name"]}
				dataSearchGetLabel={(d) => d.name}
				dataSearchGetLat={(d) => d.centerLat}
				dataSearchGetLng={(d) => d.centerLng}
				placeholder="Kiez, Adresse, Ort eingeben"
				clearCallback={() => clearLocation()}
				resultCallback={(d) => {
					addMarker(d.lng, d.lat);
					selectLocation({ lng: d.lng, lat: d.lat });
				}}
				bind:stopSearch
			/>
		</div>
	</div>
	<div class="legend-container">
		<Legend
			text={selectedToggle.legendText}
			colorScale={selectedToggle.colorScale}
			tickValues={selectedToggle?.legendTickValues}
			tickLabels={selectedToggle?.legendTickLabels}
			showScale={selectedToggle.visType !== "points"}
		/>
	</div>

	<div
		class="map-container"
		class:tooltip-shown={tooltip}
		on:mouseleave={() => {
			hoveredRegion = undefined;
			map.hoverPoint(false);
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}}
	>
		<div class="map" bind:this={elemMap} />

		{#if paywall}
			<div class="pw-map-image" />
		{/if}

		{#if !SCREENSHOT_MODE && !PWIMAGE_MODE}
			<div
				class="button-luftbilder"
				class:luftbilder={showLuftbilder}
				on:click={() => {
					showLuftbilder = !showLuftbilder;
				}}
			>
				<div class="button-luftbilder-image button-luftbilder-image-on" />
				<div class="button-luftbilder-image button-luftbilder-image-off" />
				<div class="button-luftbilder-text mobile">
					{showLuftbilder ? "Straßen-karte" : "Luft-bilder"}
				</div>
				<div class="button-luftbilder-text desktop">
					<!-- {showLuftbilder ? "Auf Straßenkarte umschalten" : "Auf Luftbilder umschalten"} -->
					{showLuftbilder ? "Straßenkarte" : "Luftbilder"}
				</div>
			</div>
		{/if}
		<div class="map-loading" class:mapLoaded>
			<div class="ts-spinner">
				<div class="ts-double-bounce1" />
				<div class="ts-double-bounce2" />
			</div>
		</div>
		{#if selectedRegion || (hoveredRegion && !hoveredRegion.filtered)}
			{@const region = hoveredRegion || selectedRegion}
			{@const hoveredAndSelectedAreSame =
				hoveredRegion && selectedRegion ? hoveredRegion.lor === selectedRegion.lor : false}
			{@const isSelected = (!hoveredRegion || hoveredAndSelectedAreSame) && !!selectedRegion}
			<div
				class="tooltip"
				on:click={tooltipClick}
				on:mouseover={tooltipMouseover}
				on:focus={() => {}}
				on:blur={() => {}}
				on:mouseout={tooltipMouseout}
				class:expanded={tooltipExpanded}
				transition:fly={{ y: 100, duration: platform === "mobile" ? 400 : 0 }}
				bind:this={tooltip}
			>
				<div class="tooltip-top">
					<div class="title-and-bezirk">
						<div class="title">{@html region.name.replace("/", "/&#8203;")}</div>
						<div class="bezirk">in {region.bezirk}</div>
					</div>
					<!-- {#if isSelected} -->
					<div class="button-close" on:click={closeClick} />
					<!-- {/if} -->
				</div>
				{#each toggleButtons as sectionSettings, i}
					{@const active = selectedToggle.id === sectionSettings.id}
					{@const prevIsActive = selectedToggleIndex === i - 1}
					<div class="rule" style:opacity={active || prevIsActive ? 0 : 1} />
					<TooltipSection
						{...sectionSettings}
						{region}
						rank={region[`rank_${sectionSettings.id}`]}
						totalValue={region[`${sectionSettings.id}_total`]}
						{active}
						on:click={() => (selectedToggleId = sectionSettings.id)}
					/>
				{/each}
			</div>
		{/if}
	</div>
	<div class="attribution">
		Daten:&nbsp;<a href="https://daten.berlin.de/datensaetze/fahrraddiebstahl-berlin" target="_blank">Polizei Berlin</a>
		Kartenbasis:&nbsp;<a href="https://www.openmaptiles.org/" target="_blank">&copy;&nbsp;OpenMapTiles</a>
		<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy;&nbsp;OpenStreetMap contributors</a>
		{#if showLuftbilder}
			| <a
				target="_blank"
				href="https://fbinter.stadt-berlin.de/fb/berlin/service.jsp?id=a_luftbild2022_true_rgbi@senstadt&amp;type=FEED"
				>Geoportal Berlin / Digitale farbige Orthophotos 2022 (DOP20RGBI)</a
			>
		{/if}
	</div>

	{#if paywall}
		<div class="pw-overlay" />
	{/if}
</div>

<style lang="scss">
	$tooltip-offset: 20vh;

	.bike-theft-map {
		font-family: $ff-sans;
		font-size: $fs-base;
		line-height: $lh-base;
		color: $color;
		user-select: none;
	}

	.map-container {
		position: relative;
		margin-bottom: 0;
		transition: margin 0.3s ease;

		@media (max-width: $breakpoint-mobile-l) {
			&.tooltip-shown {
				margin-bottom: 360px;
			}
		}
	}

	.attribution {
		font-size: 14px;
		color: #666;
		line-height: 1.5em;
		margin-top: 12px;
		text-align: right;

		a {
			text-decoration: none;
		}
	}

	.map {
		position: relative;
		height: 650px;
	}

	.ui-container-top,
	.legend-container {
		max-width: $raster-width;
		margin: 0px auto;
	}

	.ui-container-top {
		display: flex;
		justify-content: space-between;
		margin-bottom: 5px;

		@media (max-width: $breakpoint-tablet-s) {
			flex-direction: column-reverse;
			margin-bottom: 5px;

			.search {
				width: 100%;
				margin-bottom: 8px;
			}
		}

		@media (min-width: #{$breakpoint-tablet-s + 1px}) {
			.search {
				flex-basis: 250px;
			}
		}
	}

	.ui-container-bottom {
		display: flex;
		justify-content: space-between;
	}

	.headline {
		font-weight: $bold;
		font-size: 16px;
		text-align: center;
		margin-bottom: 10px;

		@media (min-width: #{$breakpoint-tablet-s + 1px}) {
			display: none;
		}
	}

	// desktop
	@media (min-width: #{$breakpoint-tablet-l + 1px}) {
	}

	// large tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
		.map {
			height: 650px;
		}
	}

	// small tablets
	@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
		.map {
			height: 650px;
		}
	}

	// large/normal phones
	@media (min-width: #{$breakpoint-mobile-m + 1px}) and (max-width: $breakpoint-mobile-l) {
		.map {
			height: 460px;
		}
	}

	// small phones
	@media (min-width: #{$breakpoint-mobile-s + 1px}) and (max-width: $breakpoint-mobile-m) {
		.map {
			height: 420px;
		}
	}

	// very small phones
	@media (max-width: $breakpoint-mobile-s) {
		.map {
			height: 420px;
		}
	}

	:global(.marker) {
		pointer-events: none;
		background-position: center bottom;
		background-size: contain;
		background-repeat: no-repeat;
		position: absolute;
		bottom: 0;
		left: 0;
		transform: translateX(-50%);
		font-weight: $bold;
		font-size: 11px;
		font-family: $ff-sans;
		line-height: 14px;
		color: #fff;
		text-shadow: 1px 1px #1f1f1f, -1px 1px #1f1f1f, -1px -1px #1f1f1f, 1px -1px #1f1f1f, 0 1px #1f1f1f, 0 -1px #1f1f1f,
			1px 0 #1f1f1f, -1px 0 #1f1f1f, 0 0 2px rgba(0, 0, 0, 0.1);
	}

	// :global(.marker.marker--bbtor) {
	// 	background-image: url(../assets/images/berlin/bbtor.svg);
	// 	width: 26px;
	// 	height: 25px;
	// }

	// :global(.marker.marker--siegessaeule) {
	// 	display: none;
	// 	background-image: url(../assets/images/berlin/siegessaeule.svg);
	// 	width: 30px;
	// 	height: 30px;
	// }

	:global(.marker.marker--fernsehturm) {
		background-image: url(../assets/images/berlin/fernsehturm.svg);
		height: 45px;
		width: 20px;
	}

	// :global(.marker.marker--kaiserwilhelm) {
	// 	background-image: url(../assets/images/berlin/kaiserwilhelm.svg);
	// 	width: 30px;
	// 	height: 30px;
	// }

	// :global(.marker.marker--oberbaum) {
	// 	background-image: url(../assets/images/berlin/oberbaum.svg);
	// 	width: 35px;
	// 	height: 30px;
	// }

	:global(.ring-marker-text) {
		position: absolute;
		font-family: $ff-sans;
		white-space: nowrap;
		font-size: 11px;
		color: #272727;
		font-weight: $bold;
		text-shadow: 1px 1px #fff, -1px 1px #fff, 1px -1px #fff, -1px -1px #fff;
		transform: translate(-100%, -100%) !important;
		pointer-events: none;
		line-height: 14px;
		text-transform: uppercase;
		letter-spacing: 0.01em;
	}

	:global(.region-popup) {
		pointer-events: none !important;
	}

	:global(.region-popup .mapboxgl-popup-content) {
		border-radius: 4px;
		font-family: $ff-sans;
		text-align: center;
		padding: 7px 9px !important;
		pointer-events: none !important;
	}

	:global(.region-popup .lor) {
		font-weight: $bold;
		font-size: 9px;
		line-height: 13px;
		letter-spacing: 0.085em;
		text-transform: uppercase;
		color: #747474;
	}

	:global(.region-popup .name) {
		font-weight: $bold;
		letter-spacing: 0.015em;
		font-size: 13px;
		line-height: 14px;
	}

	:global(.region-popup .description) {
		letter-spacing: 0.015em;
		font-size: 12px;
		line-height: 15px;
		text-transform: capitalize;
	}

	// Anpassungen mapcontrols und scale
	// loading spinner

	.map {
		z-index: 0;
	}

	.map-loading {
		position: absolute;
		background: #f2f2f2;
		left: 0;
		top: 0;
		bottom: 0;
		right: 0;
		pointer-events: all;
		z-index: 1;
		transition: opacity 0.2s ease;
		opacity: 1;

		&.mapLoaded {
			opacity: 0;
			pointer-events: none;
		}
	}

	.ts-spinner {
		width: 40px;
		height: 40px;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	.ts-double-bounce1,
	.ts-double-bounce2 {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		background-color: #333;
		opacity: 0.6;
		position: absolute;
		top: 0;
		left: 0;
		animation: sk-bounce 2s infinite ease-in-out;
	}

	.ts-double-bounce2 {
		animation-delay: -1s;
	}

	@keyframes sk-bounce {
		0%,
		100% {
			transform: scale(0);
			-webkit-transform: scale(0);
		}

		50% {
			transform: scale(1);
			-webkit-transform: scale(1);
		}
	}

	// TOOLTIP

	.tooltip {
		background: white;
		padding: 17px 20px 15px 20px;
		position: absolute;
		top: 76px;
		right: 10px;
		font-size: 14px;
		line-height: 20px;
		border-radius: 3px;
		box-shadow: 0 4px 5px rgba(0, 0, 0, 0.15);
		width: 330px;
		box-sizing: border-box;
		z-index: 1;

		@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
			width: 300px;
		}

		.rule {
			height: 1px;
			background: #cdcdcd;
			transition: opactiy 0.1s ease-in-out;
			// margin: 8px 0;
		}

		.tooltip-top {
			display: flex;
			justify-content: space-between;
		}

		.button-close {
			cursor: pointer;
			background-position: center center;
			background-image: url(../assets/images/icons/icon_close.svg);
			width: 20px;
			height: 20px;
			background-size: contain;
			flex-shrink: 0;

			// @media (max-width: $breakpoint-mobile-l) {
			// 	background-image: url(../assets/images/icons/icon_back.svg);
			// }
		}

		.lor {
			color: #8b8b8b;
			font-weight: 400;
			font-size: 11px;
			line-height: 11px;
			letter-spacing: 1px;
			text-transform: uppercase;
			margin-bottom: 3px;
		}

		.title {
			font-weight: 600;
			font-size: 18px;
			line-height: 21px;
			margin-bottom: 5px;
		}

		.bezirk {
			line-height: 12px;
			margin-bottom: 12px;
		}

		.value {
			font-weight: $medium;
		}

		.icon-label {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			position: relative;
			top: 3px;
			opacity: 0.5;
		}

		.icon-value {
			display: inline-block;
			width: 14px;
			height: 14px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			position: relative;
			top: 3px;
		}

		.icon-bad {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: 12px;
			position: relative;
			top: 3px;
			background-color: #fcece9;
			border-radius: 50%;
			margin: -1px 1px;
		}

		.row {
			white-space: nowrap;
		}

		.rowSelected {
			.label {
				font-weight: 600;
			}

			.icon-label {
				opacity: 1;
			}
		}

		@media (max-width: $breakpoint-mobile-l) {
			position: absolute;
			top: calc(100% - #{$tooltip-offset});
			width: 100%;
			left: 0;
			right: 0;
			border-radius: 10px;
			box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
			padding: 10px 20px;

			.title,
			.bezirk {
				display: inline;
			}

			.button-close {
				margin-right: 0px;
			}

			.title-and-bezirk {
				position: relative;
				padding-right: 20px;
				margin-bottom: 8px;
			}

			&:not(.expanded) {
				.rule,
				.row {
					display: none;
				}

				.rowSelected {
					display: block;

					.label {
						font-weight: normal;
					}
				}
			}
		}
	}

	// LUFTBILDER BUTTON

	.button-luftbilder {
		width: 48px;
		height: 48px;
		background: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 5px rgba(0, 0, 0, 0.25);
		position: absolute;
		top: 10px;
		right: 20px;

		&:hover {
			filter: brightness(1.2);
		}

		&.hidden {
			display: none;
		}
	}

	.button-luftbilder-image {
		position: absolute;
		left: 4px;
		top: 4px;
		width: calc(100% - 8px);
		height: calc(100% - 8px);
		border-radius: 3px;
		background-size: cover;
		background-position: center center;
		overflow: hidden;
	}

	.button-luftbilder-image-on {
		display: none;
		background-image: url(../assets/images/map/icon_data_layer.jpg);
	}

	.button-luftbilder-image-off {
		background-image: url(../assets/images/map/icon_satellite_layer.jpg);
		display: block;
	}

	.button-luftbilder.luftbilder {
		.button-luftbilder-image-on {
			display: block;
		}

		.button-luftbilder-image-off {
			display: none;
		}
	}

	.button-luftbilder-text {
		color: #6a6a6a;
		font-weight: 600;
		font-size: 11px;
		line-height: 12px;
		text-align: center;
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translate(-50%, 5px);
		text-shadow: -1px 1px 0 #fff, 1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff;

		&.desktop {
			width: 84px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.button-luftbilder-text.mobile {
			display: none;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.button-luftbilder {
			width: 38px;
			height: 38px;
			top: 10px;
			right: 10px;
		}

		.button-luftbilder-image {
			left: 3px;
			top: 3px;
			width: calc(100% - 6px);
			height: calc(100% - 6px);
		}

		.button-luftbilder-text.desktop {
			display: none;
		}
	}

	// buttons
	.button-main {
		font-weight: 500;
		text-align: center;
		// display: flex;
		align-items: center;

		// span::after {
		// 	content: attr(data-title);
		// 	font-weight: 600;
		// 	display: block;
		// 	height: 0px;
		// 	visibility: hidden;
		// }

		&.active {
			font-weight: 600;
		}
	}

	.pw-overlay {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background: transparent;
	}

	.pw-map-image {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background-image: url(../assets/images/paywall/pw-map-image-l.jpg);
		background-size: cover;
		background-repeat: no-repeat;
		background-position: right center;
		filter: blur(2px);

		@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-l) {
			background-position: right top;
			background-image: url(../assets/images/paywall/pw-map-image-m.jpg);
		}

		@media (max-width: $breakpoint-mobile-l) {
			background-position: center center;
			background-image: url(../assets/images/paywall/pw-map-image-s.jpg);
		}
	}
	@media (max-width: $breakpoint-mobile-l) {
		.pw {
			.map {
				height: 185vw !important;
			}
			.legend {
				display: none;
			}
		}
	}
</style>
